import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Divider,
  Heading3,
  InpockView,
  Paragraph1,
  Paragraph2,
  SmallText,
} from 'inpock-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { api, instance, SHIPPING_TYPE } from '@/apis';
import {
  PageTitle,
  QuantityController,
  SelectBox,
  Spinner,
  Thumbnail,
  Tooltip,
} from '@/components';
import { OoyeBanner } from '@/components';
import { Cart, CartOrderData } from '@/apis/services/cart/types';
import { CART_TEMPLATE, NON_USER_TEMPLATE, safeLocalStorage, thumbnail } from '@/utils';
import { useCartQuery, useDialog, useToast } from '@/hooks';
import { keys } from '@/hooks/queries/keys';
import { useAuth } from '@/hooks/useAuth';

import * as S from './styled';

export function CartPage() {
  const { REACT_APP_LINK_CLIENT_URL } = process.env;
  const { loggedIn, token } = useAuth();
  const [isBuying, setIsBuying] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [dialogOn, dialogOff] = useDialog();
  const toastOn = useToast();

  // 선택된 order_data 목록
  const [checkedOrderData, setCheckedOrderData] = useState<CartOrderData[]>([]);
  const checkedCartGroupIds = checkedOrderData.map((item) => item.cart_group_id);
  const { data, isLoading } = useCartQuery(checkedCartGroupIds);
  const [showInfo, setShowInfo] = useState({
    index: -1,
    show: false,
  });

  const totalOrderlineCount =
    checkedOrderData.length > 0
      ? checkedOrderData.reduce((acc, cur) => acc + cur.orderlines.length, 0)
      : 0;

  const onClickInfo = (idx: number) => {
    setShowInfo({
      index: idx,
      show: !showInfo.show,
    });
  };
  const [cartState, setCartState] = useState<Cart | null>(null);
  const cartGroupsLength = cartState
    ? cartState?.cart.reduce((acc, cur) => acc + cur.order_data.length, 0)
    : 0;

  // 전체 선택
  const onClickCheckAll = () => {
    if (cartState) {
      if (checkedOrderData.length === cartGroupsLength) {
        setCheckedOrderData([]);
      } else {
        setCheckedOrderData(
          cartState.cart.flatMap((cartItem) => {
            return cartItem.order_data.flatMap((item) => {
              const filteredOrderlines = item.orderlines.filter(
                (orderline) => orderline.is_available,
              );
              return filteredOrderlines.length > 0
                ? { ...item, orderlines: filteredOrderlines }
                : [];
            });
          }),
        );
      }
    }
  };

  // 선택삭제
  const onClickDelete = () => {
    if (!checkedCartGroupIds.length) return;
    dialogOn({
      template: CART_TEMPLATE.delete.confirm(),
      close: {
        text: '취소',
        onClick: dialogOff,
      },
      confirm: {
        text: '삭제',
        type: 'danger',
        onClick: () => {
          dialogOff();
          deleteCart(checkedCartGroupIds);
        },
      },
    });
  };

  const onClickDeleteOrderData = <T extends number | number[]>(cartGroupId: T) => {
    dialogOn({
      template: CART_TEMPLATE.delete.confirm(),
      close: {
        text: '취소',
        onClick: dialogOff,
      },
      confirm: {
        text: '삭제',
        type: 'danger',
        onClick: () => {
          dialogOff();
          deleteCart(cartGroupId);
        },
      },
    });
  };

  // 실제 삭제 액션
  const deleteCart = async <T extends number | number[]>(cartGroupIds: T) => {
    try {
      if (typeof cartGroupIds === 'object') {
        await api.cart.deleteCartGroup(cartGroupIds);
      } else if (typeof cartGroupIds === 'number') {
        await api.cart.deleteCartItem(cartGroupIds);
      }
      queryClient.refetchQueries(keys.cart.all);
      toastOn('상품 삭제 완료 😎', 2000);
      setCheckedOrderData([]);
    } catch (error) {
      const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
      const errorMessage =
        (error as AxiosError<{ message: string }>).response?.data?.message || UNKNOWN_ERROR;
      toastOn(errorMessage === UNKNOWN_ERROR ? '상품 삭제를 실패했어요' : errorMessage, 2000); // TODO : 어떻게 보여줄지 고민
    }
  };

  const onClickQuantity = async (cartItemId: number, quantity: number) => {
    const checkedOrderDataIndex = checkedOrderData.findIndex((item) =>
      item.orderlines.some((orderline) => orderline.cart_item_id === cartItemId),
    );

    try {
      await api.cart.patchCartItem(cartItemId, quantity);
      if (checkedOrderDataIndex !== -1) {
        const newOrderData = checkedOrderData[checkedOrderDataIndex].orderlines.map((orderline) => {
          if (orderline.cart_item_id === cartItemId) {
            return { ...orderline, quantity };
          }
          return orderline;
        });
        setCheckedOrderData(
          checkedOrderData.map((item, idx) => {
            if (idx === checkedOrderDataIndex) {
              return { ...item, orderlines: newOrderData };
            }
            return item;
          }),
        );
      }
      queryClient.refetchQueries(keys.cart.all);
    } catch (error) {
      toastOn('수량 변경을 실패했어요 😥', 2000);
    }
  };

  // 상품명/썸네일 클릭 시 판매페이지로 이동
  const onClickCartItem = (id: number) => {
    navigate(`/v2/products/${id}`);
  };

  // cart_item 체크
  const onClickCheckCartOrderData = (cartItem: CartOrderData) => {
    setCheckedOrderData((prev) => {
      const isExist = prev.some((item) => item.cart_group_id === cartItem.cart_group_id);
      if (isExist) {
        return prev.filter((item) => item.cart_group_id !== cartItem.cart_group_id);
      }
      // 구매 불가한 orderline filter
      const filteredOrderlines = cartItem.orderlines.filter((orderline) => orderline.is_available);
      return [...prev, { ...cartItem, orderlines: filteredOrderlines }];
    });
  };

  // 비회원 유저의 경우 로그인/비회원 구매 선택 다이얼로그
  const openNonUserDialog = (orderData: CartOrderData[], onClick: () => void) =>
    dialogOn({
      template: NON_USER_TEMPLATE.signup(onClick), // 비회원으로 구매할게요
      close: {
        text: '닫기',
        onClick: () => {
          dialogOff();
          setIsBuying(false);
        },
      },
      confirm: {
        text: '로그인',
        type: 'primary',
        onClick: () => {
          safeLocalStorage.set(
            'checkoutInfo',
            JSON.stringify({
              market_id: cartState?.market_id,
              shipping_type: SHIPPING_TYPE.GENERAL,
              order_data: orderData.map((item) => {
                return {
                  campaign_id: item.campaign_id,
                  orderlines: item.orderlines.map((orderline) => {
                    return {
                      main_option_id: orderline.main_option_id,
                      sub_option_id: orderline.sub_option_id,
                      additional_product_id: orderline.additional_product_id,
                      quantity: orderline.quantity,
                    };
                  }),
                };
              }),
            }),
          );
          window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/login?redirect=${encodeURIComponent(
            '/checkout?version=v2&from_cart=true&payment=true',
          )}`;
        },
      },
    });

  // 구매하기
  const onClickBuy = async () => {
    setIsBuying(true);
    const orderData = checkedOrderData.map((item) => {
      return {
        campaign_id: item.campaign_id,
        orderlines: item.orderlines.map((orderline) => {
          return {
            main_option_id: orderline.main_option_id,
            sub_option_id: orderline.sub_option_id,
            additional_product_id: orderline.additional_product_id,
            quantity: orderline.quantity,
          };
        }),
      };
    }) as CartOrderData[];

    const handleRedirectToCheckout = () => {
      setIsBuying(false);
      const checkoutUrl = `/checkout?from_cart=true&version=v2`;
      navigate(checkoutUrl, {
        state: {
          market_id: cartState?.market_id,
          shipping_type: SHIPPING_TYPE.GENERAL,
          order_data: orderData,
        },
      });
    };

    if (loggedIn) {
      try {
        await instance.post('/token/status', { token });
        safeLocalStorage.remove('checkoutInfo');
        setIsBuying(false);
        return handleRedirectToCheckout();
      } catch (error) {
        return openNonUserDialog(orderData, handleRedirectToCheckout);
      }
    } else {
      return openNonUserDialog(orderData, handleRedirectToCheckout);
    }
  };

  const onClickBack = () => {
    window.history.back();
  };

  useEffect(() => {
    if (data) {
      setCartState(Object.keys(data).length === 0 ? null : data);
    }
  }, [data]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>장바구니 | 인포크링크</title>
        </Helmet>
      </HelmetProvider>
      <InpockView>
        <div className="container">
          <PageTitle title="장바구니" onClick={onClickBack} />
          <S.CartHeader>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <SelectBox
                value={checkedOrderData.length > 0 && checkedOrderData.length === cartGroupsLength}
                onInput={onClickCheckAll}
              />
              <Paragraph2>전체선택</Paragraph2>
            </div>
            <div className="btn-delete" onClick={onClickDelete}>
              <SmallText color="gray500">선택삭제</SmallText>
            </div>
          </S.CartHeader>
          {isLoading ? (
            <S.SpinnerContainer>
              <Spinner color="var(--color-ui-grayscale-gray-600)" />
            </S.SpinnerContainer>
          ) : cartState && cartState.cart.length > 0 ? (
            <div style={{ marginBottom: '60px' }}>
              <Divider wide />
              <div>
                {cartState.cart.map((cartItem, cartIdx) => (
                  <Fragment key={cartIdx}>
                    <S.CartItem key={cartIdx}>
                      <S.CartItemBadge>
                        <Paragraph2 style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>
                          배송묶음
                        </Paragraph2>
                      </S.CartItemBadge>
                      <S.CartOrderDataContainer>
                        {cartItem.order_data.map((orderData, idx) => (
                          <div className="d-flex flex-column" key={orderData.cart_group_id}>
                            <div className="d-flex" style={{ gap: '12px' }}>
                              <SelectBox
                                value={checkedOrderData.some(
                                  (item) => item.cart_group_id === orderData.cart_group_id,
                                )}
                                onInput={() => onClickCheckCartOrderData(orderData)}
                                disabled={
                                  !orderData.orderlines.some((orderline) => orderline.is_available)
                                }
                              />
                              <div style={{ width: '100%' }}>
                                <S.OrderDataHeader>
                                  <div
                                    className="cart-item-title"
                                    onClick={() => onClickCartItem(orderData.campaign_id)}
                                  >
                                    <Thumbnail
                                      src={thumbnail.small(orderData.campaign_image)}
                                      alt={orderData.campaign_name}
                                    />
                                    <Paragraph2 style={{ fontWeight: '700' }}>
                                      {orderData.campaign_name}
                                    </Paragraph2>
                                  </div>
                                  <S.IconClose
                                    onClick={() =>
                                      onClickDeleteOrderData([orderData.cart_group_id])
                                    }
                                  />
                                </S.OrderDataHeader>
                                <div className="d-flex flex-column" style={{ gap: '20px' }}>
                                  {orderData.orderlines.map((orderline) => (
                                    <Fragment
                                      key={`${orderline.cart_item_id}-${orderline.main_option_id}`}
                                    >
                                      <S.OrderlineBody>
                                        <div className="d-flex justify-content-between">
                                          {orderline.main_option_name ? (
                                            <Paragraph2
                                              className={
                                                orderline.is_available
                                                  ? 'text-gray-800'
                                                  : 'text-gray-500'
                                              }
                                            >
                                              {`${orderline.main_option_name} ${
                                                orderline.sub_option_name
                                                  ? `/ ${orderline.sub_option_name}`
                                                  : ''
                                              }`}
                                            </Paragraph2>
                                          ) : orderline.additional_product_name ? (
                                            <Paragraph2
                                              className={
                                                orderline.is_available
                                                  ? 'text-gray-800'
                                                  : 'text-gray-500'
                                              }
                                            >
                                              {`[추가 상품] ${orderline.additional_product_name} / ${orderline.additional_option_name}`}
                                            </Paragraph2>
                                          ) : (
                                            <Paragraph2
                                              className={
                                                orderline.is_available
                                                  ? 'text-gray-800'
                                                  : 'text-gray-500'
                                              }
                                            >
                                              단일 옵션
                                            </Paragraph2>
                                          )}
                                          <S.IconClose
                                            onClick={() =>
                                              onClickDeleteOrderData(orderline.cart_item_id)
                                            }
                                          />
                                        </div>
                                        <S.OrderlineFooter>
                                          <QuantityController
                                            quantity={orderline.quantity}
                                            minus={{
                                              onClick: () =>
                                                onClickQuantity(
                                                  orderline.cart_item_id,
                                                  orderline.quantity - 1,
                                                ),
                                              disabled: orderline.quantity === 1,
                                            }}
                                            plus={{
                                              onClick: () =>
                                                onClickQuantity(
                                                  orderline.cart_item_id,
                                                  orderline.quantity + 1,
                                                ),
                                            }}
                                            disabled={!orderline.is_available}
                                          />
                                          <div className="price-container">
                                            {orderline.is_available ? (
                                              <Paragraph1 style={{ fontWeight: '700' }}>
                                                {orderline.product_price.toLocaleString()}원
                                              </Paragraph1>
                                            ) : (
                                              <Paragraph1 className="text-gray-500">
                                                구매불가
                                              </Paragraph1>
                                            )}
                                          </div>
                                        </S.OrderlineFooter>
                                      </S.OrderlineBody>
                                    </Fragment>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {idx !== cartItem.order_data.length - 1 && (
                              <Divider spacing={[20, 0]} />
                            )}
                          </div>
                        ))}
                        <Divider />
                        <S.DeliveryFeeContainer>
                          <Paragraph2>
                            상품 {cartItem.product_price.toLocaleString()} + 배송비{' '}
                            {cartItem.shipping_fee.toLocaleString()} ={' '}
                            <span style={{ fontWeight: '700' }}>
                              {cartItem.total_price.toLocaleString()}
                            </span>
                          </Paragraph2>
                          <Tooltip tooltipId="delivery-hint" place="top-start" openOnClick>
                            <SmallText className="text-white">
                              동일 배송정책이 적용된 상품은
                              <br />
                              묶음 배송으로 처리되며, <br />
                              배송비는 한 번만 부과됩니다.
                            </SmallText>
                          </Tooltip>
                        </S.DeliveryFeeContainer>
                      </S.CartOrderDataContainer>
                    </S.CartItem>
                    {cartIdx !== cartState.cart.length - 1 && <Divider wide />}
                  </Fragment>
                ))}
              </div>
              <Divider wide />
              <S.CartSummary>
                <div className="summary-list">
                  <Paragraph2>총 상품금액</Paragraph2>
                  <Paragraph2 fontWeight="bold">
                    {cartState.product_price.toLocaleString()}원
                  </Paragraph2>
                </div>
                <div className="summary-list">
                  <Paragraph2>총 배송비</Paragraph2>
                  <Paragraph2 fontWeight="bold">
                    {cartState.shipping_fee.toLocaleString()}원
                  </Paragraph2>
                </div>
                <div className="total-amount">
                  <Paragraph1>총 주문금액</Paragraph1>
                  <Paragraph1 className="text-orange">
                    {cartState.total_price.toLocaleString()}원
                  </Paragraph1>
                </div>
              </S.CartSummary>
              <Divider spacing={[0, 20]} wide />
              <S.Callout>
                <ul>
                  <li>
                    <Paragraph2 color="gray600">장바구니 상품은 30일 동안 보관됩니다.</Paragraph2>
                  </li>
                  <li>
                    <Paragraph2 color="gray600">
                      동일 배송정책이 적용된 상품은 묶음 배송으로 처리되며, 배송비는 한 번만
                      부과됩니다.
                    </Paragraph2>
                  </li>
                  <li>
                    <Paragraph2 color="gray600">
                      배송정책이 다른 상품은 각각 별도의 배송비가 부과됩니다.
                    </Paragraph2>
                  </li>
                </ul>
              </S.Callout>
              <div style={{ padding: '0 12px 20px' }}>
                <OoyeBanner utmContent="cart" />
              </div>
            </div>
          ) : (
            <>
              <S.EmptyCart>
                <i className="empty-icon" />
                <Heading3 color="gray400">장바구니에 담긴 상품이 없습니다.</Heading3>
              </S.EmptyCart>
              <div style={{ padding: '0 12px 20px' }}>
                <OoyeBanner utmContent="cart" />
              </div>
            </>
          )}
          {!isLoading && cartState && cartState.cart.length && (
            <S.FixedBottom>
              <Button
                type="primary"
                size="large"
                fullWidth
                disabled={!checkedOrderData || checkedOrderData.length === 0}
                loading={isBuying}
                onClick={onClickBuy}
              >
                {totalOrderlineCount ? `${totalOrderlineCount}건 구매하기` : '구매하기'}
              </Button>
            </S.FixedBottom>
          )}
        </div>
      </InpockView>
    </>
  );
}
